import { Trans } from '@lingui/react/macro';
import { Briefcase, Lock } from 'lucide-react';

import { Card, CardContent } from '~/components/ui/card';

interface AnalysisData {
  fillerWords?: string[];
  score?: number | null;
  speechPace?: string | null;
  clarity?: string | null;
  sentiment?: string | null;
  videoDurationSeconds?: number;
  personalizedTips?: string[];
}

interface HiringManagerSummaryProps {
  analysisData: AnalysisData | null;
  isLocked?: boolean;
  onUnlock?: () => void;
}

function generateSummary(data: AnalysisData): string {
  const parts: string[] = [];

  // Clarity assessment
  if (data.clarity === 'Clear') {
    parts.push('Communication is clear and easy to follow.');
  } else if (data.clarity === 'Muffled' || data.clarity === 'Unclear') {
    parts.push('Clarity could be improved — some points were hard to follow.');
  } else {
    parts.push('Communication clarity is average.');
  }

  // Pace and duration assessment
  const duration = data.videoDurationSeconds || 0;
  if (data.speechPace === 'Fast') {
    parts.push('Speaking pace feels rushed.');
  } else if (data.speechPace === 'Slow') {
    parts.push('Pace is deliberate, though could feel sluggish.');
  } else if (duration > 120) {
    parts.push('Response is on the longer side.');
  } else if (duration < 30 && duration > 0) {
    parts.push('Answer feels too brief.');
  }

  // Confidence/sentiment assessment
  if (data.sentiment === 'Positive') {
    parts.push('Confidence comes across well.');
  } else if (data.sentiment === 'Negative') {
    parts.push('Tone could be more confident.');
  } else {
    parts.push('Confidence is moderate.');
  }

  // Filler words assessment
  const fillerCount = data.fillerWords?.length || 0;
  if (fillerCount > 5) {
    parts.push('Filler words are noticeable and distract from the message.');
  } else if (fillerCount > 2) {
    parts.push('A few filler words present.');
  }

  // Overall impression based on score
  const score = data.score ?? 5;
  if (score >= 8) {
    parts.push('Overall, presents as a strong candidate.');
  } else if (score >= 6) {
    parts.push('Presents as a solid mid-level communicator.');
  } else if (score >= 4) {
    parts.push('Could benefit from more interview practice.');
  } else {
    parts.push('Needs significant preparation before interviewing.');
  }

  // Return 2-3 sentences
  return parts.slice(0, 3).join(' ');
}

export function HiringManagerSummary({
  analysisData,
  isLocked = false,
  onUnlock,
}: HiringManagerSummaryProps) {
  if (!analysisData) return null;

  const summary = generateSummary(analysisData);

  if (isLocked) {
    return (
      <Card className="border-2 border-brand-dark-blue/20 bg-gradient-to-br from-slate-50 to-white overflow-hidden">
        <CardContent className="p-5">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-brand-dark-blue/10 flex items-center justify-center">
              <Briefcase className="w-5 h-5 text-brand-dark-blue/50" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-bold text-brand-dark-blue mb-2 flex items-center gap-2">
                <Trans>Hiring Manager Summary</Trans>
                <Lock className="w-3.5 h-3.5 text-brand-gold" />
              </h3>
              <div className="relative">
                <p className="text-sm text-brand-dark-blue/60 italic leading-relaxed blur-sm select-none">
                  "Communication is clear and easy to follow. Confidence comes across well. Overall, presents as a strong candidate."
                </p>
                <div className="absolute inset-0 flex items-center justify-center">
                  {onUnlock && (
                    <button
                      onClick={onUnlock}
                      className="text-xs font-semibold text-brand-gold hover:text-brand-gold/80 transition-colors flex items-center gap-1"
                    >
                      <Lock className="w-3 h-3" />
                      <Trans>Unlock to view</Trans>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-2 border-brand-dark-blue/20 bg-gradient-to-br from-slate-50 to-white">
      <CardContent className="p-5">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-brand-dark-blue/10 flex items-center justify-center">
            <Briefcase className="w-5 h-5 text-brand-dark-blue" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-bold text-brand-dark-blue mb-2">
              <Trans>Hiring Manager Summary</Trans>
            </h3>
            <p className="text-sm text-brand-dark-blue/80 italic leading-relaxed">
              "{summary}"
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
